import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RiAlertFill } from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import _ from "lodash";

import CustomOverlayTrigger from "../../CustomOverlayTrigger";
import CustomButton from "../../CustomButton";
import InfoPop from "../../common/InfoPop";

import { isInternalUser, isInternalUserWithPartner } from "../../helpers/constants";
import { getAvailableFleetStatuses } from "../../helpers/helperSleepMode";
import { deviceStatusPopups, getDeviceDataLimit, getDeviceStatusName } from "../../helpers/helperVehicle";

import './ManageVehicles.sass'


export default function FleetOverview(props) {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'manageVehiclesPage.manageAssets' });
    const dispatch = useDispatch();
    const { goToDeviceHealth, goToManageAssets } = props;

    const { user } = useSelector(state => state.user);
    const userRole = user.roles[0];
    const { initialAssets = [], selectedAssets = [] } = useSelector(state => state.assets);
    const { fullCompantData } = useSelector(state => state.company);

    const monthlyDataAvailable = (fullCompantData?.data_usage?.available_data_pool || 0) / 1024 / 1024 / 1024;
    const monthlyDataUsed = (fullCompantData?.data_usage?.total_data_usage || 0) / 1024 / 1024 / 1024;
    const monthlyDataUsedPercentage = monthlyDataUsed * 100 / monthlyDataAvailable;
    const monthlyDataUsedDegrees = monthlyDataUsedPercentage > 100 ? 89 : monthlyDataUsedPercentage * 360 / 100 + 90;
    const isDataPoolExceeded = monthlyDataUsed > monthlyDataAvailable;

    const countDataUsage30Perc = initialAssets.reduce((errors, vehicle) => {
        return ((vehicle.data_usage / 1024 / 1024 / 1024 + vehicle.data_usage_live_stream / 1024 / 1024 / 1024) >= monthlyDataAvailable * 0.3) ? errors + 1 : errors;
    }, 0);
    const cameraHealthWarn = initialAssets.reduce((errors, vehicle) => {
        return vehicle.camera_health.warn ? errors + 1 : errors;
    }, 0);
    const cameraHealthCrit = initialAssets.reduce((errors, vehicle) => {
        return vehicle.camera_health.crit ? errors + 1 : errors;
    }, 0);
    const cameraHealthWarnSD20 = initialAssets.reduce((errors, vehicle) => {
        return vehicle.camera_health.warn_sd20 ? errors + 1 : errors;
    }, 0);
    const cameraErrors = initialAssets.reduce((errors, vehicle) => {
        return errors + vehicle.cameraError
    }, 0);

    const setSelectedAssets = useCallback((payload) => {
        dispatch({ type: 'SET_SELECTED_ASSETS', payload })
    }, [dispatch]);

    const setSortProps = useCallback((payload) => {
        dispatch({ type: 'SET_SORT_PROPS', payload })
    }, [dispatch]);

    const renderFleetActivityStatus = () => {
        const res = []
        const initStatuses = getAvailableFleetStatuses({ driving: 0, sleep: 0, offline: 0, parked: 0, idling: 0, awake: 0, disabled: 0 }, 'glance')

        const fleetStatuses = initialAssets.reduce((result, device) => {
            result[device.status] = (result[device.status] || 0) + 1
            return result
        }, { ...initStatuses })

        const fleetStatusesKeys = Object.keys(fleetStatuses)
        fleetStatusesKeys.forEach((status_name) => {
            const title_status_name = i18n.t(`homePage.fleetStatuses.${getDeviceStatusName(status_name).replace(' ', '_')}`, getDeviceStatusName(status_name));
            res.push(
                <div key={status_name} className="status-row">
                    <div className={`icon ${status_name.replace(' ', '_')}`} />
                    <InfoPop
                        title={`${t('labelActivityStatus')} - ${title_status_name.charAt(0).toUpperCase() + title_status_name.slice(1)}`}
                        body={deviceStatusPopups()[status_name]}
                    >
                        <span className="title">{title_status_name}</span>
                    </InfoPop>
                    {fleetStatuses[status_name]}
                </div>
            )
        })

        return res
    }

    const selectDevicesByError = (errType) => {
        let newSelectedAssets = [];
        switch (errType) {
            case 'channel_status':
                newSelectedAssets = initialAssets.filter(vehicle => vehicle.cameraError)
                setSortProps({
                    sortAssetsName: 'cameraError',
                    sortAssetsOrder: 'desc',
                });
                break;
            case 'sd_status':
                newSelectedAssets = initialAssets.filter(vehicle => vehicle.recordingError)
                setSortProps({
                    sortAssetsName: 'recordingError',
                    sortAssetsOrder: 'desc',
                });
                break;
            case 'data_limit':
                newSelectedAssets = initialAssets.filter(vehicle => (vehicle.data_usage / 1024 / 1024 / 1024 + vehicle.data_usage_live_stream / 1024 / 1024 / 1024) > (getDeviceDataLimit(vehicle.data_limit, fullCompantData)));
                setSortProps({
                    sortAssetsName: 'data_usage',
                    sortAssetsOrder: 'desc',
                });
                break;
            case 'live_stream':
                newSelectedAssets = initialAssets.filter(vehicle => vehicle.liveStreamError)
                setSortProps({
                    sortAssetsName: 'liveStreamError',
                    sortAssetsOrder: 'desc',
                });
                break;
            case 'no_sd_card':
                newSelectedAssets = initialAssets.filter(vehicle => !!vehicle.no_sd_card)
                setSortProps({
                    sortAssetsName: 'no_sd_card',
                    sortAssetsOrder: 'desc',
                });
                break;
            default:
                break;
        }

        newSelectedAssets = newSelectedAssets.map(vehicle => vehicle.device_id);
        if (_.isEqual(newSelectedAssets, selectedAssets)) {
            setSelectedAssets([])
        } else {
            newSelectedAssets.length && setSelectedAssets(newSelectedAssets)
        }
        goToManageAssets();
    }

    return (
        <div className="fleet-overview">
            <main className="main-content-block">
                <div className="at-a-glance">
                    <h3 className="category-title">{t('titleFleetOverview')}</h3>
                    <div className="cards-wrapper">
                        <div className="card">
                            <div className="header">{t('headerFleetStatus')}</div>
                            <div className="body">
                                <div className='fleet-activity-status'>
                                    {renderFleetActivityStatus()}
                                </div>
                                <div className="total-cameras">{initialAssets.length}</div>
                                <div className="text-center">{t('textTotalDevices')}</div>
                            </div>
                        </div>
                        <div className={`card${isDataPoolExceeded ? ' card-error' : ''}`}>
                            <div className="header">{t('headerDataUsage')}</div>
                            <div className="body">
                                <div className="ellipse-graph">
                                    <div className="radius"
                                        style={{ transform: `rotate(${monthlyDataUsedDegrees}deg)` }} />
                                    <div className="data-usage">
                                        <div>
                                            <div>{`${monthlyDataUsedPercentage.toFixed(1)} %`}</div>
                                            <div>{`${monthlyDataUsed.toFixed(1)} GB`}</div>
                                            <div style={{ fontSize: '11px' }}>{t('textUsed')}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="data-pool-wrapper">
                                    <div className="total-data-pool">
                                        <div className="value">{`${monthlyDataAvailable.toFixed(1)} GB`}</div>
                                        <div className="text-center">{t('textTotalData')}</div>
                                    </div>
                                    {isDataPoolExceeded
                                        ? (
                                            <CustomOverlayTrigger
                                                {...{
                                                    type: 'crit',
                                                    title: t('overlayTriggerCritTitle'),
                                                    body: t('overlayTriggerCritBody', { monthlyDataAvailable: monthlyDataAvailable.toFixed(1) })
                                                }}
                                                placement="top"
                                                trigger="click"
                                                closeBtn={true}
                                                prefix='crit'
                                                bodyPrefix='text-center'
                                            >
                                                <div>
                                                    <CustomButton variant="link">
                                                        <RiAlertFill className="alert-error" />
                                                    </CustomButton>
                                                </div>
                                            </CustomOverlayTrigger>
                                        )
                                        : (countDataUsage30Perc > 0 &&
                                            <CustomOverlayTrigger
                                                {...{
                                                    type: 'warn',
                                                    title: t('overlayTriggerWarnTitle'),
                                                    body: t('overlayTriggerWarnBody', { countDataUsage30Perc })
                                                }}
                                                placement="top"
                                                trigger="click"
                                                closeBtn={true}
                                                prefix='warn'
                                                bodyPrefix='text-center'
                                            >
                                                <div>
                                                    <CustomButton variant="link">
                                                        <RiAlertFill />
                                                    </CustomButton>
                                                </div>
                                            </CustomOverlayTrigger>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                        {(isInternalUserWithPartner(userRole) || userRole === 'fleet_manager') &&
                            <div className="card">
                                <div className="header">{t('headerDeviceHealth')}</div>
                                <div className="body camera-health">
                                    {((isInternalUser(userRole) && cameraHealthWarn > 0) || (cameraHealthWarnSD20 > 0)) &&
                                        <OverlayTrigger
                                            placement="bottom"
                                            delayHide={100}
                                            overlay={
                                                <Tooltip id="tooltip" className="tooltip custom-tooltips">
                                                    {`${isInternalUser(userRole) ? cameraHealthWarn : cameraHealthWarnSD20} ${t('camera')}${(isInternalUser(userRole) && cameraHealthWarn > 1 || cameraHealthWarnSD20 > 1) ? 's' : ''} ${t('warningHealth')}`}
                                                </Tooltip>
                                            }
                                        >
                                            <div className="camera-health-row">
                                                <CustomButton
                                                    variant="primary"
                                                    size="hs"
                                                    prefix="warn"
                                                    onClick={goToDeviceHealth}
                                                >
                                                    {t('WARN')}
                                                </CustomButton>
                                                <span
                                                    className="value">{isInternalUser(userRole) ? cameraHealthWarn : cameraHealthWarnSD20}</span>
                                            </div>
                                        </OverlayTrigger>
                                    }
                                    {isInternalUser(userRole) && cameraHealthCrit > 0 &&
                                        <OverlayTrigger
                                            placement="bottom"
                                            delayHide={100}
                                            overlay={
                                                <Tooltip id="tooltip" className="tooltip custom-tooltips">
                                                    {`${cameraHealthCrit} ${t('camera')}${cameraHealthCrit > 1 ? 's' : ''} ${t('criticalHealth')}`}
                                                </Tooltip>
                                            }
                                        >
                                            <div className="camera-health-row">
                                                <CustomButton
                                                    variant="primary"
                                                    size="hs"
                                                    prefix="crit"
                                                    onClick={goToDeviceHealth}
                                                >
                                                    {t('CRITICAL')}
                                                </CustomButton>
                                                <span className="value">{cameraHealthCrit}</span>
                                            </div>
                                        </OverlayTrigger>
                                    }
                                    {cameraErrors > 0 &&
                                        <OverlayTrigger
                                            placement="bottom"
                                            delayHide={100}
                                            overlay={
                                                <Tooltip id="tooltip" className="tooltip custom-tooltips">
                                                    {`${cameraErrors} ${cameraErrors > 1 ? t('camerasHave') : t('cameraHas')} ${t('statusIssues')}`}
                                                </Tooltip>}
                                        >
                                            <div className="camera-health-row">
                                                <CustomButton
                                                    variant="primary"
                                                    size="hs"
                                                    prefix="crit"
                                                    onClick={() => selectDevicesByError('channel_status')}
                                                >
                                                    {t('CHANNEL')}
                                                </CustomButton>
                                                <span className="value">{cameraErrors}</span>
                                            </div>
                                        </OverlayTrigger>
                                    }
                                    {(
                                        (isInternalUser(userRole) && cameraHealthWarn === 0 && cameraHealthCrit === 0 && cameraErrors === 0)
                                        || (!isInternalUser(userRole) && cameraHealthWarnSD20 === 0 && cameraErrors === 0)
                                    ) && <div><img src="/images/health-no-issue.png" /></div>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </main>
        </div>
    )
}
